import { Component, OnInit, Input, inject, output } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ApplyPanelComponent, MediaObserverService, ButtonDirective } from '@imagise/shared';
import { EditMode, FilterTypes } from '../../../domain/enums/editor.enum';
import { EditorOptions } from '../../../domain/interfaces/model-options';
import { Size2D } from '../../../domain/interfaces/shared';
import { MessagingService } from '../../../infrastructure/messaging/messaging.service';
import { EditorFilterArgs, EditorFilterDraftOperation } from '../../../domain';
import { RefreshEditOptionsMessage, PreviewImageLoadedMessage } from '../../../domain/models/messages';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule, TooltipPosition } from '@angular/material/tooltip';
import { MatRippleModule } from '@angular/material/core';
import {
  BackendFilterOptionsComponent,
  BackendFilterOptionsConfig,
} from './backend-filter-options/backend-filter-options.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PanelLayoutComponent } from '../panel-layout/panel-layout.component';

@Component({
  selector: 'lib-filter-panel',
  host: {
    class: 'contents',
  },
  templateUrl: './filter-panel.component.html',
  styleUrls: ['./filter-panel.component.scss'],
  imports: [
    MatIconModule,
    MatTooltipModule,
    MatRippleModule,
    BackendFilterOptionsComponent,
    ApplyPanelComponent,
    ButtonDirective,
    PanelLayoutComponent,
    TranslateModule,
  ],
})
export class FilterPanelComponent implements OnInit {
  private readonly translate = inject(TranslateService);
  private readonly messageBus = inject(MessagingService);
  private readonly mediaObserver = inject(MediaObserverService);
  private readonly currentBreakpoint = toSignal(this.mediaObserver.breakpoint$);

  constructor() {
    this.messageBus
      .of(RefreshEditOptionsMessage)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.refresh());
    this.messageBus
      .of(PreviewImageLoadedMessage)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        if (this.trySelectFilterFromOptions()) {
          this.editorOptions = null;
        }
      });
  }

  @Input() set imageSize(value: Size2D | null) {
    if (value) {
      this.trySelectFilterFromOptions();
    }
  }

  /**
   * Editor options
   */
  @Input() editorOptions: EditorOptions | null = null;

  buttons: IFilterTypeModel[] = [
    {
      filterType: FilterTypes.BigRectangular,
      title: 'FILTERS.BIG_RECTANGULAR.TITLE',
      description: this.translate.instant('FILTERS.BIG_RECTANGULAR.DESCRIPTION'),
    },
    {
      filterType: FilterTypes.GaussianBlur,
      title: 'FILTERS.GAUSSIAN_BLUR.TITLE',
      description: this.translate.instant('FILTERS.GAUSSIAN_BLUR.DESCRIPTION'),
      options: {
        showSize: true,
        showSigma: true,
        defaultSize: 3,
        defaultSigma: 1,
      },
    },
    {
      filterType: FilterTypes.SmallRectangular,
      title: 'FILTERS.SMALL_RECTANGULAR.TITLE',
      description: this.translate.instant('FILTERS.SMALL_RECTANGULAR.DESCRIPTION'),
    },
    {
      filterType: FilterTypes.Median,
      title: 'FILTERS.MEDIAN.TITLE',
      description: this.translate.instant('FILTERS.MEDIAN.DESCRIPTION'),
      options: {
        showSize: true,
        defaultSize: 3,
      },
    },
    {
      filterType: FilterTypes.GaussWiener,
      title: 'FILTERS.GAUSS_WIENER.TITLE',
      description: this.translate.instant('FILTERS.GAUSS_WIENER.DESCRIPTION'),
      options: {
        showSize: true,
        showSigma: true,
        defaultSize: 3,
        defaultSigma: 1,
      },
    },
    {
      filterType: FilterTypes.MotionWiener,
      title: 'FILTERS.MOTION_WIENER.TITLE',
      description: this.translate.instant('FILTERS.MOTION_WIENER.DESCRIPTION'),
      options: {
        showSize: true,
        showSigma: true,
        showAngle: true,
        defaultSize: 5,
        defaultSigma: 1,
        defaultAngle: 0,
      },
    },
    {
      filterType: FilterTypes.Sharpen,
      title: 'FILTERS.SHARPEN.TITLE',
      description: this.translate.instant('FILTERS.SHARPEN.DESCRIPTION'),
      options: {
        showSize: true,
        showSigma: true,
        defaultSize: 3,
        defaultSigma: 1,
      },
    },
    {
      filterType: FilterTypes.BilateralSmoothing,
      title: 'FILTERS.BILATERAL_SMOOTHING.TITLE',
      description: this.translate.instant('FILTERS.BILATERAL_SMOOTHING.DESCRIPTION'),
      options: {
        showSize: true,
        defaultSize: 3,
      },
    },
  ];

  readonly changed = output<EditorFilterDraftOperation | null>();

  readonly apply = output();

  readonly cancelRequested = output<void>();

  selectedType: IFilterTypeModel | null = null;

  filterTypes = FilterTypes;

  private lastFilterArgs: EditorFilterArgs | null = null;

  get tooltipPosition(): TooltipPosition {
    const breakpoint = this.currentBreakpoint();
    return breakpoint && this.mediaObserver.isBreakpointUp(breakpoint, 'xl') ? 'right' : 'below';
  }

  onFilterTypeSelected(model: IFilterTypeModel): void {
    this.selectedType = model;

    this.lastFilterArgs = null;
    if (!this.isFilterWithOptions(model)) {
      this.changed.emit(this.toFilterDraft(model.filterType, null));
    }
  }

  onFilterArgsChanged(args: EditorFilterArgs): void {
    if (!this.selectedType) {
      return;
    }

    this.lastFilterArgs = args;
    this.changed.emit(this.toFilterDraft(this.selectedType.filterType, args));
  }

  applyFilterOptions(): void {
    this.hideFilterOptionsPanel();
    this.apply.emit();
  }

  onCancel() {
    this.hideFilterOptionsPanel();
    this.refresh();
    this.cancelRequested.emit();
  }

  onBackToFilterList(): void {
    this.hideFilterOptionsPanel();
    this.refresh();
  }

  hideFilterOptionsPanel(): void {
    this.selectedType = null;
    this.lastFilterArgs = null;
  }

  ngOnInit(): void {
    this.refresh();
  }

  private refresh(): void {
    if (this.selectedType) {
      this.changed.emit(this.toFilterDraft(this.selectedType.filterType, this.lastFilterArgs));
    } else {
      this.changed.emit(null);
    }
  }

  private toFilterDraft(filterType: FilterTypes, filterArgs: EditorFilterArgs | null): EditorFilterDraftOperation {
    return {
      kind: 'filter',
      mode: EditMode.Filter,
      params: { filterType, filterArgs },
    };
  }

  private isFilterWithOptions(filter: Pick<IFilterTypeModel, 'options'>): boolean {
    return Boolean(filter.options);
  }

  private trySelectFilterFromOptions(): boolean {
    if (!this.editorOptions || !this.editorOptions.filterType) return false;
    //@ts-expect-error enum number
    const filterType = FilterTypes[this.editorOptions.filterType];
    if (filterType === FilterTypes.None) return false;
    const filterBtn = this.buttons.find(b => b.filterType === filterType);
    if (!filterBtn) {
      return false;
    }
    const args = this.lastFilterArgs;
    this.onFilterTypeSelected(filterBtn);
    if (args) this.onFilterArgsChanged(args);
    return true;
  }
}

export interface IFilterTypeModel {
  title: string;
  description: string;
  filterType: FilterTypes;
  options?: BackendFilterOptionsConfig;
}
